import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { Shield, Package, ArrowLeft, Lock, Mail, Eye, EyeOff, Waves, Leaf, Fingerprint, X, CheckCircle, AlertCircle, Hammer } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import LanguageToggle from '../components/LanguageToggle';
import { useLanguage } from '../contexts/LanguageContext';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const checkPasswordRules = (pwd) => ({
  length:    pwd.length >= 8,
  uppercase: /[A-Z]/.test(pwd),
  lowercase: /[a-z]/.test(pwd),
  number:    /[0-9]/.test(pwd),
  special:   /[^A-Za-z0-9]/.test(pwd),
});

const PasswordRule = ({ ok, label }) => (
  <div className={`flex items-center gap-1.5 text-xs ${ok ? 'text-emerald-600' : 'text-gray-400'}`}>
    {ok ? <CheckCircle className="h-3 w-3" /> : <div className="h-3 w-3 rounded-full border border-gray-300" />}
    {label}
  </div>
);

const TABS = [
  {
    id: 'blue',
    icon: Waves,
    labelTr: 'Blue Zone', labelEn: 'Blue Zone',
    descTr: 'Akredite kuruluş girişi', descEn: 'Accredited organization login',
    color: 'from-blue-600 to-cyan-500',
    btnClass: 'bg-blue-600 hover:bg-blue-700',
    passkeyClass: 'bg-blue-500/10 hover:bg-blue-500/20 text-blue-600 border border-blue-200',
    allowedRoles: ['customer_admin', 'sub_user'],
    // Phase 86AY.6 — zone gate. Without this a green_zone user could login
    // from the Blue Zone tab (role matches). Spec: zones are strictly siloed.
    allowedZones: ['blue_zone'],
    bypassRole: 'customer_admin',
    redirectFn: (data) => `/musteri-admin/${data.tenant_slug || 'default'}/dashboard`,
  },
  {
    id: 'green',
    icon: Leaf,
    labelTr: 'Green Zone', labelEn: 'Green Zone',
    descTr: 'Sivil toplum & medya girişi', descEn: 'Civil society & media login',
    color: 'from-emerald-600 to-green-500',
    btnClass: 'bg-emerald-600 hover:bg-emerald-700',
    passkeyClass: 'bg-emerald-500/10 hover:bg-emerald-500/20 text-emerald-600 border border-emerald-200',
    allowedRoles: ['customer_admin', 'sub_user'],
    allowedZones: ['green_zone'],
    bypassRole: 'customer_admin',
    redirectFn: (data) => `/musteri-admin/${data.tenant_slug || 'default'}/dashboard`,
  },
  {
    id: 'supplier',
    icon: Package,
    labelTr: 'Tedarikçi', labelEn: 'Supplier',
    descTr: 'Tedarikçi panel girişi', descEn: 'Supplier dashboard login',
    color: 'from-amber-500 to-orange-500',
    btnClass: 'bg-amber-500 hover:bg-amber-600',
    passkeyClass: 'bg-amber-500/10 hover:bg-amber-500/20 text-amber-600 border border-amber-200',
    allowedRoles: ['supplier_admin'],
    bypassRole: 'supplier_admin',
    redirectFn: (data) => `/tedarikci-admin/${data.tenant_slug || 'default'}/dashboard`,
  },
  {
    id: 'contractor',
    icon: Hammer,
    labelTr: 'Yüklenici', labelEn: 'Contractor',
    descTr: 'Yüklenici panel girişi', descEn: 'Contractor dashboard login',
    color: 'from-orange-600 to-red-600',
    btnClass: 'bg-orange-600 hover:bg-orange-700',
    passkeyClass: 'bg-orange-500/10 hover:bg-orange-500/20 text-orange-600 border border-orange-200',
    allowedRoles: ['contractor_admin'],
    bypassRole: 'contractor_admin',
    redirectFn: (data) => `/yuklenci-admin/${data.tenant_slug || 'default'}/dashboard`,
  },
  {
    id: 'ultra',
    icon: Shield,
    labelTr: 'Ultra Admin', labelEn: 'Ultra Admin',
    descTr: 'Platform yönetim paneli', descEn: 'Platform management panel',
    color: 'from-purple-700 to-purple-900',
    btnClass: 'bg-purple-700 hover:bg-purple-800',
    passkeyClass: 'bg-purple-500/10 hover:bg-purple-500/20 text-purple-700 border border-purple-200',
    allowedRoles: ['ultra_admin'],
    bypassRole: 'ultra_admin',
    redirectFn: () => '/ultra-admin/dashboard',
  },
];

export default function LoginHub() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { language } = useLanguage();
  const tr = language === 'tr';

  // Dil değişince URL'i güncelle (/giris ↔ /login)
  useEffect(() => {
    const isLoginPath = location.pathname === '/giris' || location.pathname === '/login';
    if (!isLoginPath) return;
    const targetPath = language === 'tr' ? '/giris' : '/login';
    if (location.pathname !== targetPath) {
      // Phase 86AZ.60 — Preserve `?tab=...&only=...` so that locked
      // supplier-only sign-in keeps working across the TR/EN swap.
      navigate(`${targetPath}${location.search || ''}`, { replace: true });
    }
  }, [language]); // eslint-disable-line

  const defaultTab = searchParams.get('tab') || 'blue';
  // Phase 86AZ.60 — When `only` is set (e.g. /giris?tab=supplier&only=1),
  // lock the page to that single role: no tab switcher, no link to other
  // sign-up flows. Used by the public supplier landing page.
  const onlyParam = (searchParams.get('only') || '').toLowerCase();
  const locked = !!onlyParam;
  const [activeTab, setActiveTab] = useState(defaultTab);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPass, setShowPass] = useState(false);
  const [rememberMe, setRememberMe] = useState(true); // Test aşaması: varsayılan açık
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Forgot password modal state
  const [forgotOpen, setForgotOpen] = useState(false);
  const [fpStep, setFpStep] = useState('email'); // 'email' | 'code' | 'done'
  const [fpEmail, setFpEmail] = useState('');
  const [fpCode, setFpCode] = useState('');
  const [fpNewPass, setFpNewPass] = useState('');
  const [fpConfirmPass, setFpConfirmPass] = useState('');
  const [fpShowPass, setFpShowPass] = useState(false);
  const [fpShowConfirm, setFpShowConfirm] = useState(false);
  const [fpPassTouched, setFpPassTouched] = useState(false);
  const [fpConfirmTouched, setFpConfirmTouched] = useState(false);
  const [fpLoading, setFpLoading] = useState(false);
  const [fpError, setFpError] = useState('');

  const openForgot = () => {
    setFpStep('email');
    setFpEmail(email);
    setFpCode('');
    setFpNewPass('');
    setFpConfirmPass('');
    setFpPassTouched(false);
    setFpConfirmTouched(false);
    setFpError('');
    setForgotOpen(true);
  };

  const handleFpSendCode = async (e) => {
    e.preventDefault();
    setFpLoading(true);
    setFpError('');
    try {
      await axios.post(`${API}/auth/forgot-password`, { email: fpEmail });
      setFpStep('code');
    } catch (err) {
      setFpError(err.response?.data?.detail || (tr ? 'Bir hata oluştu.' : 'An error occurred.'));
    } finally {
      setFpLoading(false);
    }
  };

  const fpPasswordRules = checkPasswordRules(fpNewPass);
  const fpAllRulesOk = Object.values(fpPasswordRules).every(Boolean);
  const fpPasswordsMatch = fpNewPass === fpConfirmPass;

  const handleFpReset = async (e) => {
    e.preventDefault();
    setFpPassTouched(true); setFpConfirmTouched(true);
    if (!fpAllRulesOk) { setFpError(tr ? 'Şifre kurallarını karşılamıyor.' : 'Password does not meet requirements.'); return; }
    if (!fpPasswordsMatch) { setFpError(tr ? 'Şifreler uyuşmuyor.' : 'Passwords do not match.'); return; }
    setFpLoading(true);
    setFpError('');
    try {
      await axios.post(`${API}/auth/reset-password`, { email: fpEmail, code: fpCode, new_password: fpNewPass });
      setFpStep('done');
    } catch (err) {
      setFpError(err.response?.data?.detail || (tr ? 'Bir hata oluştu.' : 'An error occurred.'));
    } finally {
      setFpLoading(false);
    }
  };

  const tab = TABS.find(t => t.id === activeTab) || TABS[0];
  const Icon = tab.icon;

  const saveAndRedirect = (data) => {
    const tokenKey = activeTab === 'ultra' ? 'cop31_ultra_token' : activeTab === 'supplier' ? 'cop31_supplier_token' : activeTab === 'contractor' ? 'cop31_contractor_token' : 'cop31_token';
    const userKey  = activeTab === 'ultra' ? 'cop31_ultra_user'  : activeTab === 'supplier' ? 'cop31_supplier_user'  : activeTab === 'contractor' ? 'cop31_contractor_user' : 'cop31_user';
    const timeKey  = activeTab === 'ultra' ? 'cop31_ultra_login_time' : activeTab === 'supplier' ? 'cop31_supplier_login_time' : 'cop31_login_time';
    // Her zaman localStorage'a yaz (49 yer okuyor)
    localStorage.setItem(tokenKey, data.access_token);
    localStorage.setItem(userKey, JSON.stringify(data));
    localStorage.setItem(timeKey, String(Date.now()));
    if (rememberMe) {
      // Kalıcı — session marker'ı kaldır
      sessionStorage.removeItem(`session_only_${tokenKey}`);
    } else {
      // Sadece bu sekme açıkken — sekme kapılınca App.js temizler
      sessionStorage.setItem(`session_only_${tokenKey}`, '1');
    }
    navigate(tab.redirectFn(data));
  };

  const handleTabChange = (id) => { setActiveTab(id); setError(''); setEmail(''); setPassword(''); };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const res = await axios.post(`${API}/auth/login`, { email, password });
      const data = res.data;
      if (!tab.allowedRoles.includes(data.role)) {
        const roleLabel = { ultra_admin: 'Ultra Admin', supplier_admin: tr ? 'Tedarikçi' : 'Supplier', customer_admin: 'Blue/Green Zone' };
        setError(tr
          ? `Bu hesap ${tab.labelTr} girişi için değil. Doğru sekmeyi seçin (${roleLabel[data.role] || data.role}).`
          : `This account is not for ${tab.labelEn}. Please select the correct tab (${roleLabel[data.role] || data.role}).`);
        return;
      }
      // Phase 86AY.6 — Zone gate. A green_zone customer can authenticate
      // (role matches) but MUST NOT enter through the Blue Zone tab and
      // vice versa. Show a friendly hint that points to the right tab.
      if (tab.allowedZones && data.zone && !tab.allowedZones.includes(data.zone)) {
        const zoneLabel = { blue_zone: 'Blue Zone', green_zone: 'Green Zone' };
        const correctTab = zoneLabel[data.zone] || data.zone;
        setError(tr
          ? `Bu hesap ${correctTab} kullanıcısı. Lütfen ${correctTab} sekmesinden giriş yapın.`
          : `This account belongs to ${correctTab}. Please sign in from the ${correctTab} tab.`);
        return;
      }
      saveAndRedirect(data);
    } catch (err) {
      const detail = err.response?.data?.detail || '';
      if (detail.includes('onaylanmamış') || detail.includes('pending')) {
        setError(tr ? 'Başvurunuz henüz onaylanmamıştır.' : 'Your application has not been approved yet.');
      } else if (detail.includes('pasif')) {
        setError(tr ? 'Hesabınız pasif durumda.' : 'Your account is inactive.');
      } else {
        setError(tr ? 'Geçersiz e-posta veya şifre.' : 'Invalid email or password.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handlePasskey = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await axios.post(`${API}/auth/login-bypass`, { role: tab.bypassRole });
      saveAndRedirect(res.data);
    } catch (err) {
      setError(err.response?.data?.detail || (tr ? 'Giriş başarısız.' : 'Login failed.'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col notranslate" translate="no">
      {/* Forgot Password Modal */}
      {forgotOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm px-4">
          <div className="bg-white rounded-3xl shadow-2xl w-full max-w-sm overflow-hidden">
            {/* Aktif tab renginde üst çizgi */}
            <div className={`h-1.5 bg-gradient-to-r ${tab.color}`} />
            <div className="p-6">
              <div className="flex items-center justify-between mb-5">
                <div className="flex items-center gap-2.5">
                  <div className={`h-8 w-8 rounded-xl bg-gradient-to-br ${tab.color} flex items-center justify-center`}>
                    <Lock className="h-4 w-4 text-white" />
                  </div>
                  <h3 className="font-bold text-gray-900 text-lg">{tr ? 'Şifremi Sıfırla' : 'Reset Password'}</h3>
                </div>
                <button onClick={() => setForgotOpen(false)} className="text-gray-400 hover:text-gray-700"><X className="h-5 w-5" /></button>
              </div>

              {fpStep === 'email' && (
                <form onSubmit={handleFpSendCode} className="space-y-4">
                  <p className="text-sm text-gray-500">{tr ? 'E-posta adresinize doğrulama kodu göndereceğiz.' : 'We will send a verification code to your email.'}</p>
                  {fpError && <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-xl px-4 py-3">{fpError}</div>}
                  <div>
                    <Label className="text-sm text-gray-600 mb-1.5 block">{tr ? 'E-posta Adresi' : 'Email Address'}</Label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                      <Input type="email" value={fpEmail} onChange={e => setFpEmail(e.target.value)} required placeholder="ornek@firma.com" className="pl-10 rounded-xl border-gray-200" />
                    </div>
                  </div>
                  <Button type="submit" disabled={fpLoading} className={`w-full ${tab.btnClass} text-white rounded-xl py-5`}>
                    {fpLoading ? <span className="flex items-center justify-center gap-2"><span className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" />{tr ? 'Gönderiliyor...' : 'Sending...'}</span> : (tr ? 'Kod Gönder' : 'Send Code')}
                  </Button>
                </form>
              )}

              {fpStep === 'code' && (
                <form onSubmit={handleFpReset} className="space-y-4">
                  <p className="text-sm text-gray-500">{tr ? `${fpEmail} adresine 6 haneli kod gönderdik.` : `We sent a 6-digit code to ${fpEmail}.`}</p>
                  {fpError && <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-xl px-4 py-3 flex items-center gap-2"><AlertCircle className="h-4 w-4 flex-shrink-0" />{fpError}</div>}

                  {/* Verification code */}
                  <div>
                    <Label className="text-sm text-gray-600 mb-1.5 block">{tr ? 'Doğrulama Kodu' : 'Verification Code'}</Label>
                    <Input
                      value={fpCode}
                      onChange={e => setFpCode(e.target.value.replace(/\D/g,'').slice(0,6))}
                      required
                      placeholder={tr ? '_ _ _ _ _ _  kodunuzu girin' : '_ _ _ _ _ _  enter your code'}
                      maxLength={6}
                      autoComplete="one-time-code"
                      className="text-center text-xl tracking-[0.4em] font-bold rounded-xl border-gray-200 placeholder:text-gray-300 placeholder:text-sm placeholder:tracking-normal"
                    />
                  </div>

                  {/* New password */}
                  <div>
                    <Label className="text-sm text-gray-600 mb-1.5 block">{tr ? 'Yeni Şifre' : 'New Password'}</Label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                      <Input
                        type={fpShowPass ? 'text' : 'password'}
                        value={fpNewPass}
                        onChange={e => setFpNewPass(e.target.value)}
                        onBlur={() => setFpPassTouched(true)}
                        required
                        placeholder="••••••••"
                        autoComplete="new-password"
                        className={`pl-10 pr-10 rounded-xl ${fpPassTouched && !fpAllRulesOk && fpNewPass ? 'border-red-400' : 'border-gray-200'}`}
                      />
                      <button type="button" onClick={() => setFpShowPass(!fpShowPass)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                        {fpShowPass ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </button>
                    </div>
                    {(fpPassTouched || fpNewPass.length > 0) && (
                      <div className="grid grid-cols-2 gap-1 mt-2">
                        <PasswordRule ok={fpPasswordRules.length}    label={tr ? 'En az 8 karakter' : 'Min 8 chars'} />
                        <PasswordRule ok={fpPasswordRules.uppercase} label={tr ? 'Büyük harf' : 'Uppercase'} />
                        <PasswordRule ok={fpPasswordRules.lowercase} label={tr ? 'Küçük harf' : 'Lowercase'} />
                        <PasswordRule ok={fpPasswordRules.number}    label={tr ? 'Rakam' : 'Number'} />
                        <PasswordRule ok={fpPasswordRules.special}   label={tr ? 'Özel karakter' : 'Special char'} />
                      </div>
                    )}
                  </div>

                  {/* Confirm password */}
                  <div>
                    <Label className="text-sm text-gray-600 mb-1.5 block">{tr ? 'Şifre Tekrar' : 'Confirm Password'}</Label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                      <Input
                        type={fpShowConfirm ? 'text' : 'password'}
                        value={fpConfirmPass}
                        onChange={e => setFpConfirmPass(e.target.value)}
                        onBlur={() => setFpConfirmTouched(true)}
                        required
                        placeholder="••••••••"
                        autoComplete="new-password"
                        className={`pl-10 pr-10 rounded-xl ${fpConfirmTouched && fpConfirmPass && !fpPasswordsMatch ? 'border-red-400' : fpConfirmTouched && fpPasswordsMatch && fpConfirmPass ? 'border-emerald-400' : 'border-gray-200'}`}
                      />
                      <button type="button" onClick={() => setFpShowConfirm(!fpShowConfirm)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                        {fpShowConfirm ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </button>
                    </div>
                    {fpConfirmTouched && fpConfirmPass && !fpPasswordsMatch && (
                      <p className="text-xs text-red-500 flex items-center gap-1 mt-1"><AlertCircle className="h-3 w-3" />{tr ? 'Şifreler uyuşmuyor.' : 'Passwords do not match.'}</p>
                    )}
                    {fpConfirmTouched && fpConfirmPass && fpPasswordsMatch && fpAllRulesOk && (
                      <p className="text-xs text-emerald-600 flex items-center gap-1 mt-1"><CheckCircle className="h-3 w-3" />{tr ? 'Şifreler eşleşiyor.' : 'Passwords match.'}</p>
                    )}
                  </div>

                  <Button type="submit" disabled={fpLoading || fpCode.length < 6 || !fpAllRulesOk || !fpPasswordsMatch} className={`w-full ${tab.btnClass} text-white rounded-xl py-5`}>
                    {fpLoading ? <span className="flex items-center justify-center gap-2"><span className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" />{tr ? 'Güncelleniyor...' : 'Updating...'}</span> : (tr ? 'Şifreyi Güncelle' : 'Update Password')}
                  </Button>
                  <button type="button" onClick={() => setFpStep('email')} className="w-full text-sm text-gray-400 hover:text-gray-600 py-1">{tr ? '← Geri dön' : '← Go back'}</button>
                </form>
              )}

              {fpStep === 'done' && (
                <div className="text-center py-4 space-y-4">
                  <div className={`h-16 w-16 rounded-full bg-gradient-to-br ${tab.color} flex items-center justify-center mx-auto`}>
                    <CheckCircle className="h-8 w-8 text-white" />
                  </div>
                  <p className="font-semibold text-gray-900">{tr ? 'Şifreniz güncellendi!' : 'Password updated!'}</p>
                  <p className="text-sm text-gray-500">{tr ? 'Yeni şifrenizle giriş yapabilirsiniz.' : 'You can now sign in with your new password.'}</p>
                  <Button onClick={() => { setForgotOpen(false); setPassword(''); }} className={`w-full ${tab.btnClass} text-white rounded-xl py-5`}>
                    {tr ? 'Giriş Yap' : 'Sign In'}
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Top bar */}
      <div className="flex items-center justify-between px-6 py-4 bg-white border-b border-gray-100 shadow-sm">
        <div className="w-24" />
        <div className="font-playfair text-2xl tracking-tight text-[#0A3A5A]">SummitFlow</div>
        <LanguageToggle variant="admin" />
      </div>

      {/* Center content */}
      <div className="flex-1 flex items-center justify-center px-4 py-6 sm:py-10">
        <div className={`flex flex-col md:flex-row gap-5 w-full ${locked ? 'max-w-md justify-center' : 'max-w-2xl'} items-stretch md:items-start`}>

          {/* ── Left (md+) / Top (mobile): title + tabs ── */}
          {!locked && (
          <div className="flex-shrink-0 w-full md:w-44 pt-1">
            <div className="mb-4 md:mb-6">
              <h1 className="font-playfair text-2xl md:text-3xl text-[#0A3A5A] font-medium leading-tight tracking-tight">{tr ? 'Giriş Yap' : 'Sign In'}</h1>
              <p className="text-[#4A5568] text-xs mt-1">{tr ? 'Erişim türünü seçin' : 'Choose your access type'}</p>
            </div>
            <div className="grid grid-cols-2 md:flex md:flex-col gap-2">
              {TABS.map((t) => {
                const TIcon = t.icon;
                const active = activeTab === t.id;
                return (
                  <button
                    key={t.id}
                    onClick={() => handleTabChange(t.id)}
                    className={`flex items-center gap-2 md:gap-3 px-3 md:px-3.5 py-2.5 md:py-3 rounded-2xl transition-all duration-200 text-left w-full ${
                      active
                        ? 'bg-white shadow-md border border-gray-200'
                        : 'bg-gray-100 hover:bg-gray-200 text-gray-600'
                    }`}
                  >
                    <div className={`h-7 w-7 md:h-8 md:w-8 rounded-xl flex items-center justify-center flex-shrink-0 bg-gradient-to-br ${t.color}`}>
                      <TIcon className="h-4 w-4 text-white" />
                    </div>
                    <span className={`text-xs md:text-sm font-semibold leading-tight ${active ? 'text-gray-800' : 'text-gray-500'}`}>
                      {language === 'tr' ? t.labelTr : t.labelEn}
                    </span>
                    {active && <div className="ml-auto h-2 w-2 rounded-full bg-emerald-500 flex-shrink-0" />}
                  </button>
                );
              })}
              {/* Ana Sayfa butonu */}
              <button
                onClick={() => navigate('/')}
                className="flex items-center gap-2 md:gap-3 px-3 md:px-3.5 py-2.5 md:py-3 rounded-2xl transition-all duration-200 text-left w-full bg-gray-100 hover:bg-gray-200 text-gray-600 col-span-2 md:col-span-1 md:mt-1 md:border-t md:border-gray-200 md:pt-3"
              >
                <div className="h-7 w-7 md:h-8 md:w-8 rounded-xl flex items-center justify-center flex-shrink-0 bg-gradient-to-br from-gray-500 to-gray-600">
                  <ArrowLeft className="h-4 w-4 text-white" />
                </div>
                <span className="text-xs md:text-sm font-semibold leading-tight text-gray-500">
                  {tr ? 'Ana Sayfa' : 'Home'}
                </span>
              </button>
            </div>
          </div>
          )}

          {/* ── Right (md+) / Bottom (mobile): login card ── */}
          <div className="flex-1 min-w-0">
          <div className="bg-white rounded-3xl shadow-2xl overflow-hidden">
            <div className={`h-1.5 bg-gradient-to-r ${tab.color}`} />
            <div className="p-5 sm:p-8">
              {/* Tab info */}
              <div className="flex items-center gap-3 mb-6">
                <div className={`h-10 w-10 rounded-xl bg-gradient-to-br ${tab.color} flex items-center justify-center`}>
                  <Icon className="h-5 w-5 text-white" />
                </div>
                <div>
                  <h2 className="font-bold text-gray-900">{language === 'tr' ? tab.labelTr : tab.labelEn}</h2>
                  <p className="text-xs text-gray-500">{language === 'tr' ? tab.descTr : tab.descEn}</p>
                </div>
              </div>

              {error && (
                <div className="mb-4 bg-red-50 border border-red-200 text-red-700 text-sm rounded-xl px-4 py-3">
                  {error}
                </div>
              )}

              {/* Email + Password form */}
              <form key={activeTab} onSubmit={handleLogin} className="space-y-4">
                <div>
                  <Label className="text-sm text-gray-600 mb-1.5 block">{tr ? 'E-posta Adresi' : 'Email Address'}</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <Input
                      type="email"
                      value={email}
                      onChange={e => setEmail(e.target.value)}
                      placeholder={tr ? 'ornek@firma.com' : 'example@company.com'}
                      required
                      autoComplete="off"
                      className="pl-10 rounded-xl border-gray-200"
                      data-testid="login-email"
                    />
                  </div>
                </div>

                <div>
                  <Label className="text-sm text-gray-600 mb-1.5 block">{tr ? 'Şifre' : 'Password'}</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <Input
                      type={showPass ? 'text' : 'password'}
                      value={password}
                      onChange={e => setPassword(e.target.value)}
                      placeholder="••••••••"
                      required
                      autoComplete="new-password"
                      className="pl-10 pr-10 rounded-xl border-gray-200"
                      data-testid="login-password"
                    />
                    <button type="button" onClick={() => setShowPass(!showPass)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                      {showPass ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                </div>

                {/* Remember me + Forgot password */}
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <div className="flex items-center space-x-2">
                    <Checkbox id="remember" checked={rememberMe} onCheckedChange={setRememberMe} data-testid="remember-me" />
                    <Label htmlFor="remember" className="text-sm text-gray-500 cursor-pointer">
                      {tr ? 'Beni Hatırla' : 'Remember me'}
                    </Label>
                  </div>
                  <button type="button" onClick={openForgot} className="text-sm text-emerald-600 hover:text-emerald-700 font-medium whitespace-nowrap" data-testid="forgot-password">
                    {tr ? 'Şifremi Unuttum' : 'Forgot password?'}
                  </button>
                </div>

                <Button
                  type="submit"
                  disabled={loading}
                  className={`w-full ${tab.btnClass} text-white rounded-xl py-5 font-semibold text-base`}
                  data-testid="login-submit"
                >
                  {loading
                    ? <span className="flex items-center justify-center gap-2"><span className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" />{tr ? 'Giriş yapılıyor...' : 'Signing in...'}</span>
                    : (tr ? 'Giriş Yap' : 'Sign In')}
                </Button>
              </form>

              {/* Divider */}
              <div className="relative my-5">
                <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-gray-100" /></div>
                <div className="relative flex justify-center"><span className="bg-white px-3 text-xs text-gray-400">{tr ? 'veya' : 'or'}</span></div>
              </div>

              {/* Passkey button */}
              <button
                onClick={handlePasskey}
                disabled={loading}
                data-testid="passkey-btn"
                className={`w-full ${tab.passkeyClass} font-semibold py-3 rounded-xl transition-all flex items-center justify-center gap-2 text-sm disabled:opacity-60`}
              >
                <Fingerprint className="h-4 w-4" />
                {tr ? 'Passkey ile Giriş' : 'Sign in with Passkey'}
              </button>

              {/* Register link */}
              {(activeTab === 'blue' || activeTab === 'green' || activeTab === 'supplier' || activeTab === 'contractor') && (
                <p className="text-center text-sm text-gray-500 mt-4">
                  {tr ? 'Hesabınız yok mu? ' : "Don't have an account? "}
                  <button
                    onClick={() => navigate(
                      activeTab === 'green' ? '/green-zone-kayit' :
                      activeTab === 'supplier' ? '/tedarikci-kayit' :
                      activeTab === 'contractor' ? '/yuklenci-kayit' : '/kayit'
                    )}
                    className="text-emerald-600 hover:text-emerald-700 font-medium"
                  >
                    {tr ? 'Kayıt Ol' : 'Register'}
                  </button>
                </p>
              )}
            </div>
          </div>
          <p className="text-center text-gray-400 text-xs mt-4">© {new Date().getFullYear()} SummitFlow</p>
          </div>

        </div>
      </div>
    </div>
  );
}
